import { useMemo, useState } from 'react';
import { AppDataGrid } from '../../components/AppDataGrid';
import { RESULT_GRID_NO_ZEBRA } from './resultGrids';
import { useHotkeys } from '../../components/hotkeys';
import {
  REE_SEL_TABS, pendingBatchCols, pendingJobCols, searchRows,
  type PendingJob, type ReePendingBatch, type ReeSelTab,
} from './resultEntryPending';

export interface ResultEntryPendingSelectorProps {
  tab: ReeSelTab;
  onTabChange: (tab: ReeSelTab) => void;
  jobs: PendingJob[];
  batches: ReePendingBatch[];
  selectedJobIds: Set<string | number>;
  onJobSelectionChange: (ids: Set<string | number>) => void;
  selectedBatchIds: Set<string | number>;
  onBatchSelectionChange: (ids: Set<string | number>) => void;
  loading: boolean;
  /** Double-click / Enter on a row: select only that row and Load it. */
  onLoadRow: (tab: ReeSelTab, id: string) => void;
  /** Visible (filtered + sorted) rows of the active tab — feeds the page's Export. */
  onVisibleJobsChange?: (rows: PendingJob[]) => void;
  onVisibleBatchesChange?: (rows: ReePendingBatch[]) => void;
  /** Show the QC Batch tab's "Pending Samples" column (default true — REE). RV / RA
   *  pass false: their facet links batches to jobs, not samples, so no count. */
  showBatchSampleCount?: boolean;
}


/**
 * Pending selection panel — shared by Result Entry Express, Result Validation and
 * Result Approval (RTCFR 2026-09-27 / 2026-09-28). Shown in place of the form's
 * result grid until a Job / QC Batch is loaded; each form supplies its OWN stage's
 * pending jobs + batches (never REE's "Pending Entry" on RV/RA):
 *
 *   [🔍 fuzzy search …]            [⛶]  ← compact toolbar, top of the panel (RTCFR 2026-09-28)
 *   Sample [n] · QC Batch [n]           ← tabs, orange count badges
 *   shared <AppDataGrid>                 ← multi-select; double-click / Enter loads
 *   Page X of N · Page size             ← the grid's shared footer
 *
 * The one search box filters the ACTIVE tab only (the badge counts stay the
 * pending totals). The grid's own header strip is suppressed — Export lives in
 * the page's control bar and Expand is the toolbar button, which fullscreens the
 * whole panel (tabs + search + grid) exactly like the result detail's does.
 */
export function ResultEntryPendingSelector({
  tab, onTabChange, jobs, batches,
  selectedJobIds, onJobSelectionChange, selectedBatchIds, onBatchSelectionChange,
  loading, onLoadRow, onVisibleJobsChange, onVisibleBatchesChange,
  showBatchSampleCount = true,
}: ResultEntryPendingSelectorProps) {
  const jobCols   = useMemo(() => pendingJobCols(), []);
  const batchCols = useMemo(() => pendingBatchCols(showBatchSampleCount), [showBatchSampleCount]);
  const count = (t: ReeSelTab) => (t === 'Sample' ? jobs.length : batches.length);

  const [search, setSearch] = useState('');
  const shownJobs    = useMemo(() => searchRows(jobs, jobCols, search), [jobs, jobCols, search]);
  const shownBatches = useMemo(() => searchRows(batches, batchCols, search), [batches, batchCols, search]);

  const [maximized, setMaximized] = useState(false);
  // Esc restores from full screen — via the shared hotkey layer (standing rule 14).
  useHotkeys(maximized
    ? { combo: 'esc', label: 'Exit full screen', group: 'View', run: () => setMaximized(false) }
    : null);

  return (
    // SAME wrapper / panel / toolbar-row / search markup + classes as the result
    // detail grid's panel (ResultEntryDetail inline: .ree-detail-inline ›
    // .ree-inline-panel › .ree-inline-save-row › .list-search 260px ›
    // .ree-inline-btns) so the two stages look identical — no separate search
    // style (RTCFR 2026-09-28, "copy the detail grid's fuzzy-search design").
    // The ree-pending-* classes are hooks only (no styles of their own).
    <div className={`ree-detail-inline ree-pending-wrap${maximized ? ' ree-detail-maximized' : ''}`}>
      <div className="bl-card bl-card-flex ree-inline-panel ree-pending-panel">
        {/* 1 · Search / Expand toolbar — TOP of the panel (RTCFR 2026-09-28). */}
        <div className="ree-inline-save-row ree-pending-toolbar">
          <div className="list-search" style={{ width: 260, flexShrink: 0 }}>
            <span className="tb-i">🔍</span>
            <input type="text" aria-label="Search pending list" data-unsaved-ignore
              placeholder={tab === 'Sample'
                ? 'Search job, project, client…'
                : 'Search QC batch, test, method, client…'}
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && (
              <span className="list-search-clear" role="button" aria-label="Clear search"
                onClick={() => setSearch('')}>×</span>
            )}
          </div>
          <div className="ree-inline-btns">
            <button type="button" className="ree-maximize-btn"
              onClick={() => setMaximized(m => !m)}
              title={maximized ? 'Restore to inline view. (Esc)' : 'Expand to full page.'}
              aria-label={maximized ? 'Restore' : 'Maximize'}>
              <i className={`ti ${maximized ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* 2 · Sample | QC Batch tabs */}
        <div className="ree-tab-bar" role="tablist" aria-label="Pending selection">
          {REE_SEL_TABS.map(t => {
            const active = tab === t;
            return (
              <button key={t} type="button" role="tab" aria-selected={active}
                className={`ree-tab${active ? ' ree-tab-active' : ''}`}
                onClick={() => onTabChange(t)}>
                {t}
                <span className="ree-tab-badge" aria-label={loading ? 'loading' : `${count(t)} pending`}>
                  {loading ? '…' : count(t)}
                </span>
              </button>
            );
          })}
        </div>

        {/* 3 · grid (+ 4 · its shared pagination / page-size footer) */}
        {tab === 'Sample' ? (
          <AppDataGrid<PendingJob>
            className={RESULT_GRID_NO_ZEBRA}
            key="ree-pending-jobs"
            rows={shownJobs}
            columns={jobCols}
            getRowId={r => r.jobID}
            hideHeaderTools
            selectable
            selectedIds={selectedJobIds}
            onSelectionChange={onJobSelectionChange}
            onRowDoubleClick={r => onLoadRow('Sample', r.jobID)}
            onVisibleRowsChange={onVisibleJobsChange}
            initialSort={{ key: 'receivedDate', dir: 'desc' }}
            loading={loading}
            emptyText={search.trim()
              ? `No pending jobs match “${search.trim()}”`
              : 'No pending jobs for the selected Date Range and filters'}
          />
        ) : (
          <AppDataGrid<ReePendingBatch>
            className={RESULT_GRID_NO_ZEBRA}
            key="ree-pending-batches"
            rows={shownBatches}
            columns={batchCols}
            getRowId={r => r.qcBatchID}
            hideHeaderTools
            selectable
            selectedIds={selectedBatchIds}
            onSelectionChange={onBatchSelectionChange}
            onRowDoubleClick={r => onLoadRow('QC Batch', r.qcBatchID)}
            onVisibleRowsChange={onVisibleBatchesChange}
            initialSort={{ key: 'qcBatchID', dir: 'desc' }}
            loading={loading}
            emptyText={search.trim()
              ? `No pending QC batches match “${search.trim()}”`
              : 'No pending QC batches for the selected Date Range and filters'}
          />
        )}
      </div>
    </div>
  );
}
